import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Users, Search, Mail, Trash2, ArrowRight, Check, Send, Ticket, Link2, Copy, RefreshCw, Edit2, Clock, XCircle, ChevronDown, ChevronUp, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Positions by sport
const POSITIONS = {
  soccer: [
    'Goalkeeper', 'Right Back', 'Left Back', 'Center Back', 
    'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder',
    'Right Winger', 'Left Winger', 'Striker', 'Forward'
  ]
};

const CoachAthletes = () => {
  const { getAuthHeaders, user } = useAuth();
  const isPerformanceCoach = user?.coach_role === 'private_performance';
  const [athletes, setAthletes] = useState([]);
  const [athletePasses, setAthletePasses] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [showPendingInvites, setShowPendingInvites] = useState(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [passFilter, setPassFilter] = useState('all'); // all, active, pending, inactive
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteMode, setInviteMode] = useState('email'); // 'email' or 'link'
  const [generatedLink, setGeneratedLink] = useState('');
  const [inviteData, setInviteData] = useState({
    email: '', athlete_name: '', athlete_age: '', birth_date: '', athlete_position: '', athlete_sport: 'soccer'
  });
  const [submitting, setSubmitting] = useState(false);
  const [editingInviteEmail, setEditingInviteEmail] = useState(null);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [athletesRes, passesRes, invitesRes] = await Promise.all([
        axios.get(`${API}/athletes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/athlete-passes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/coach/invites`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setAthletes(athletesRes.data);
      setPendingInvites(invitesRes.data || []);
      setAthletePasses(passesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  // Invite management functions
  const handleResendInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/coach/invites/${inviteId}/resend`, null, { headers: getAuthHeaders() });
      toast.success('Invite resent successfully');
    } catch (error) {
      toast.error('Failed to resend invite');
    }
  };

  const handleUpdateInviteEmail = async (inviteId) => {
    if (!newEmail) return;
    try {
      await axios.put(`${API}/coach/invites/${inviteId}/email?email=${encodeURIComponent(newEmail)}`, null, { headers: getAuthHeaders() });
      toast.success('Email updated');
      setEditingInviteEmail(null);
      setNewEmail('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update email');
    }
  };

  const handleRegenerateLink = async (inviteId) => {
    try {
      const res = await axios.post(`${API}/coach/invites/${inviteId}/regenerate`, null, { headers: getAuthHeaders() });
      const newLink = `${window.location.origin}${res.data.invite_link}`;
      navigator.clipboard.writeText(newLink);
      toast.success('New link generated and copied!');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate new link');
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!window.confirm('Cancel this invite?')) return;
    try {
      await axios.delete(`${API}/coach/invites/${inviteId}`, { headers: getAuthHeaders() });
      toast.success('Invite cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  const copyInviteLink = (inviteId) => {
    const link = `${window.location.origin}/invite/${inviteId}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied!');
  };

  // Get Athlete Pass status for an athlete
  const getAthletePassStatus = (athleteId) => {
    const pass = athletePasses.find(p => p.athlete_id === athleteId);
    return pass ? pass.status : 'none';
  };

  // Get Athlete Pass for an athlete
  const getAthletePass = (athleteId) => {
    return athletePasses.find(p => p.athlete_id === athleteId);
  };

  // Create Athlete Pass
  const handleCreatePass = async (athleteId) => {
    try {
      await axios.post(`${API}/athlete-passes`, { athlete_id: athleteId }, { headers: getAuthHeaders() });
      toast.success('Athlete Pass created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Athlete Pass');
    }
  };

  // Send payment link
  const handleSendPaymentLink = async (athleteId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/athlete-passes/payment-link/${athleteId}`, { headers: getAuthHeaders() });
      const link = response.data.payment_link;
      
      // Try to copy to clipboard, if it fails, show a modal or alert with the link
      try {
        await navigator.clipboard.writeText(link);
        toast.success(`Payment link copied! ($${response.data.amount}/month)`);
      } catch (clipboardError) {
        // Fallback: show the link in a prompt
        const athleteName = response.data.athlete_name || 'Athlete';
        toast.success(
          <div className="space-y-2">
            <p className="font-medium">Payment link for {athleteName}:</p>
            <p className="text-xs break-all bg-muted p-2 rounded">{link}</p>
            <p className="text-xs text-muted-foreground">Copy this link and send to the parent</p>
          </div>,
          { duration: 10000 }
        );
      }
    } catch (error) {
      console.error('Payment link error:', error);
      toast.error(error.response?.data?.detail || 'Failed to get payment link');
    }
  };

  // Send payment link via email
  const handleSendPaymentEmail = async (athleteId, parentEmail) => {
    if (!parentEmail) {
      toast.error('No parent email available for this athlete');
      return;
    }
    
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/athlete-passes/send-payment-email`, {
        athlete_id: athleteId,
        parent_email: parentEmail
      }, { headers: getAuthHeaders() });
      
      toast.success(`Payment link sent to ${parentEmail}!`);
    } catch (error) {
      console.error('Send email error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        athlete_name: inviteData.athlete_name,
        athlete_age: inviteData.athlete_age ? parseInt(inviteData.athlete_age) : undefined,
        birth_date: inviteData.birth_date || undefined,
        athlete_position: inviteData.athlete_position,
        generate_link_only: inviteMode === 'link'
      };
      
      // Only include email if in email mode
      if (inviteMode === 'email') {
        payload.email = inviteData.email;
      }
      
      const response = await axios.post(`${API}/auth/invite`, payload, { headers: getAuthHeaders() });
      
      const inviteUrl = `${window.location.origin}${response.data.invite_link}`;
      
      if (inviteMode === 'link') {
        // Just show the generated link
        setGeneratedLink(inviteUrl);
        toast.success('Invite link generated!');
      } else {
        // Email mode - copy link and show appropriate message
        await navigator.clipboard.writeText(inviteUrl);
        
        if (response.data.email_sent) {
          toast.success('Invite sent by email and link copied!');
        } else {
          toast.success('Invite created! Link copied (email not configured).');
        }
        setShowInviteForm(false);
        setInviteData({ email: '', athlete_name: '', athlete_age: '', birth_date: '', athlete_position: '', athlete_sport: 'soccer' });
        setGeneratedLink('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    }
    setSubmitting(false);
  };

  const handleDelete = async (athleteId, athleteName) => {
    if (!window.confirm(`Are you sure you want to remove ${athleteName}?`)) return;
    
    try {
      await axios.delete(`${API}/athletes/${athleteId}`, { headers: getAuthHeaders() });
      toast.success('Athlete removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove athlete');
    }
  };

  const getPlanLabel = (athlete) => {
    if (athlete.plan_type === 'package') {
      return 'Package';
    }
    return athlete.plan_type === 'monthly' ? 'Monthly' : 'Quarterly';
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-500/20 text-green-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'overdue': return 'bg-red-500/20 text-red-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPassStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'inactive': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-muted/50 text-muted-foreground border-border';
    }
  };

  const getPassStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Pass Active';
      case 'pending': return 'Pass Pending';
      case 'inactive': return 'Pass Inactive';
      default: return 'No Pass';
    }
  };

  // Filter athletes
  const filteredAthletes = athletes.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.position.toLowerCase().includes(search.toLowerCase());
    
    if (passFilter === 'all') return matchesSearch;
    
    const passStatus = getAthletePassStatus(a.id);
    if (passFilter === 'none') return matchesSearch && passStatus === 'none';
    return matchesSearch && passStatus === passFilter;
  });

  // Count by pass status
  const passStatusCounts = {
    all: athletes.length,
    active: athletes.filter(a => getAthletePassStatus(a.id) === 'active').length,
    pending: athletes.filter(a => getAthletePassStatus(a.id) === 'pending').length,
    inactive: athletes.filter(a => getAthletePassStatus(a.id) === 'inactive').length,
    none: athletes.filter(a => getAthletePassStatus(a.id) === 'none').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coach-athletes">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Athletes</h1>
          <p className="text-muted-foreground">{athletes.length} athletes total</p>
        </div>
        <div className="flex gap-2">
          <Button className="bg-primary text-black hover:bg-primary/90" onClick={() => setShowInviteForm(true)} data-testid="invite-athlete-btn">
            <Mail className="mr-2 h-4 w-4" />
            Invite Athlete
          </Button>
        </div>
      </div>

      {/* Pending Invites Section */}
      {pendingInvites.length > 0 && (
        <div className="bg-card border border-border/40 rounded-sm overflow-hidden">
          <button
            onClick={() => setShowPendingInvites(!showPendingInvites)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Clock className="text-yellow-400" size={20} />
              <span className="font-heading text-lg uppercase">Pending Invites</span>
              <span className="bg-yellow-500/20 text-yellow-400 text-xs px-2 py-0.5 rounded-full">
                {pendingInvites.length}
              </span>
            </div>
            {showPendingInvites ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          
          {showPendingInvites && (
            <div className="border-t border-border/40 divide-y divide-border/40">
              {pendingInvites.map((invite) => (
                <div key={invite.id} className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{invite.athlete_name}</span>
                        <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                          Not Accepted
                        </span>
                      </div>
                      {editingInviteEmail === invite.id ? (
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="New email"
                            className="bg-transparent h-8 text-sm"
                          />
                          <Button size="sm" onClick={() => handleUpdateInviteEmail(invite.id)} className="h-8">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingInviteEmail(null); setNewEmail(''); }} className="h-8">Cancel</Button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{invite.email}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Sent {new Date(invite.created_at).toLocaleDateString()}
                        {invite.resent_at && ` • Resent ${new Date(invite.resent_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResendInvite(invite.id)}
                        title="Resend email"
                      >
                        <RefreshCw size={14} className="mr-1" /> Resend
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditingInviteEmail(invite.id); setNewEmail(invite.email); }}
                        title="Change email"
                      >
                        <Edit2 size={14} className="mr-1" /> Edit Email
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyInviteLink(invite.id)}
                        title="Copy link"
                      >
                        <Copy size={14} className="mr-1" /> Copy Link
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRegenerateLink(invite.id)}
                        title="Generate new link"
                      >
                        <Link2 size={14} className="mr-1" /> New Link
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteInvite(invite.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        title="Cancel invite"
                      >
                        <XCircle size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
          <Input
            placeholder="Search athletes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-transparent"
          />
        </div>
        
        {/* Athlete Pass Filter */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-muted-foreground flex items-center gap-2">
            <Ticket size={16} />
            Athlete Pass:
          </span>
          {[
            { key: 'all', label: 'All' },
            { key: 'active', label: 'Active' },
            { key: 'pending', label: 'Pending' },
            { key: 'none', label: 'No Pass' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPassFilter(key)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                passFilter === key
                  ? 'bg-primary text-black'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {label} ({passStatusCounts[key]})
            </button>
          ))}
          <span className="text-xs text-muted-foreground ml-2 hidden md:inline">
            (Athlete Pass is a platform subscription - payments managed by Meybra)
          </span>
        </div>
      </div>

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-md">
            <h2 className="font-heading text-xl uppercase mb-4">Invite Parent</h2>

            {/* V1: contextual notice for Performance Coaches — sets expectations
                that their evaluations only cover physical attributes. */}
            {isPerformanceCoach && (
              <div className="mb-4 rounded-sm border border-orange-400/30 bg-orange-400/5 p-3 text-xs leading-relaxed" data-testid="invite-performance-notice">
                <p className="text-orange-300 font-semibold uppercase tracking-wider text-[10px] mb-1">Performance Coach Notice</p>
                <span className="text-muted-foreground">
                  You are inviting this athlete as a <span className="text-foreground font-medium">Performance Coach</span>.
                  Your evaluations will be limited to the <span className="text-foreground font-medium">physical domain</span> (speed, agility, explosiveness, endurance).
                  Consider asking the family to also connect a <span className="text-foreground font-medium">Soccer Coach</span> for full technical/tactical coverage.
                </span>
              </div>
            )}
            
            {/* Mode Toggle */}
            <div className="flex mb-4 border border-border rounded-sm overflow-hidden">
              <button
                type="button"
                onClick={() => { setInviteMode('email'); setGeneratedLink(''); }}
                className={`flex-1 py-2 text-sm uppercase tracking-wider transition-colors ${
                  inviteMode === 'email' ? 'bg-primary text-black' : 'bg-transparent hover:bg-muted'
                }`}
              >
                <Mail size={14} className="inline mr-1" /> Via Email
              </button>
              <button
                type="button"
                onClick={() => setInviteMode('link')}
                className={`flex-1 py-2 text-sm uppercase tracking-wider transition-colors ${
                  inviteMode === 'link' ? 'bg-primary text-black' : 'bg-transparent hover:bg-muted'
                }`}
              >
                <Link2 size={14} className="inline mr-1" /> Generate Link
              </button>
            </div>
            
            <form onSubmit={handleInvite} className="space-y-4">
              {inviteMode === 'email' && (
                <Input 
                  type="email" 
                  placeholder="Parent's Email" 
                  value={inviteData.email} 
                  onChange={(e) => setInviteData({...inviteData, email: e.target.value})} 
                  required 
                />
              )}
              <Input 
                placeholder="Athlete's Name" 
                value={inviteData.athlete_name} 
                onChange={(e) => setInviteData({...inviteData, athlete_name: e.target.value})} 
                required 
              />
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  Birth Date <span className="text-xs text-primary">(used to compute age & avoid duplicates)</span>
                </label>
                <Input
                  type="date"
                  value={inviteData.birth_date}
                  onChange={(e) => {
                    const birth = e.target.value;
                    let computedAge = '';
                    if (birth) {
                      const d = new Date(birth);
                      const today = new Date();
                      let age = today.getFullYear() - d.getFullYear();
                      const m = today.getMonth() - d.getMonth();
                      if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
                      if (age >= 0 && age < 120) computedAge = String(age);
                    }
                    setInviteData({ ...inviteData, birth_date: birth, athlete_age: computedAge });
                  }}
                  max={new Date().toISOString().split('T')[0]}
                  data-testid="invite-birth-date"
                />
                {inviteData.athlete_age && (
                  <p className="text-xs text-primary mt-1" data-testid="invite-computed-age">
                    Age: <span className="font-bold">{inviteData.athlete_age}</span> years old
                  </p>
                )}
              </div>
              
              {/* Sport field removed - Soccer only platform */}

              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Position</label>
                <select 
                  value={inviteData.athlete_position} 
                  onChange={(e) => setInviteData({...inviteData, athlete_position: e.target.value})} 
                  required
                  className="w-full p-3 bg-transparent border border-border rounded-sm"
                >
                  <option value="" className="bg-card">Select Position</option>
                  {POSITIONS['soccer'].map(pos => (
                    <option key={pos} value={pos} className="bg-card">{pos}</option>
                  ))}
                </select>
              </div>

              {/* Generated Link Display */}
              {generatedLink && inviteMode === 'link' && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-3">
                  <p className="text-sm text-green-400 mb-2 flex items-center gap-2">
                    <Check size={14} /> Link generated successfully!
                  </p>
                  <div className="flex gap-2">
                    <Input 
                      value={generatedLink} 
                      readOnly 
                      className="text-xs bg-transparent"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedLink);
                        toast.success('Link copied to clipboard!');
                      }}
                      className="bg-green-500 text-white hover:bg-green-600"
                    >
                      <Copy size={14} />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Share this link with the parent to complete registration
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowInviteForm(false);
                    setGeneratedLink('');
                    setInviteMode('email');
                  }} 
                  className="flex-1"
                >
                  {generatedLink ? 'Close' : 'Cancel'}
                </Button>
                {!generatedLink && (
                  <Button type="submit" disabled={submitting} className="flex-1 bg-primary text-black">
                    {submitting ? 'Creating...' : inviteMode === 'email' ? 'Send Invite' : 'Generate Link'}
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Athletes List */}
      {filteredAthletes.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-2">{search ? 'No athletes found' : 'No athletes yet'}</p>
          {!search && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Send an invite to a parent to add their athlete
              </p>
              <Button onClick={() => setShowInviteForm(true)} data-testid="empty-invite-btn">
                <Mail className="mr-2 h-4 w-4" />
                Invite First Athlete
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-card border border-border/40 rounded-sm divide-y divide-border/40">
          {filteredAthletes.map((athlete) => {
            const passStatus = getAthletePassStatus(athlete.id);
            const athletePass = getAthletePass(athlete.id);
            
            return (
              <div key={athlete.id} className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                <Link to={`/coach/athletes/${athlete.id}`} className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center relative">
                    <span className="text-primary font-semibold text-lg">{athlete.name[0]}</span>
                    {/* Pass status indicator dot */}
                    <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-card ${
                      passStatus === 'active' ? 'bg-green-500' :
                      passStatus === 'pending' ? 'bg-yellow-500' :
                      passStatus === 'inactive' ? 'bg-red-500' : 'bg-muted'
                    }`} title={getPassStatusLabel(passStatus)} />
                  </div>
                  <div>
                    <p className="font-medium">{athlete.name}</p>
                    <p className="text-sm text-muted-foreground">{athlete.position} • {athlete.age} years • {athlete.sport}</p>
                    {/* Parent Info */}
                    {athlete.parent_name && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <User size={10} className="inline mr-1" />
                        {athlete.parent_name}
                        {athlete.parent_email && <span className="ml-2 text-primary">{athlete.parent_email}</span>}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {/* Athlete Pass Badge */}
                      <span className={`text-xs px-2 py-0.5 rounded border ${getPassStatusColor(passStatus)}`}>
                        <Ticket size={10} className="inline mr-1" />
                        {getPassStatusLabel(passStatus)}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-muted rounded">{getPlanLabel(athlete)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded capitalize ${getPaymentStatusColor(athlete.payment_status)}`}>
                        {athlete.payment_status || 'pending'}
                      </span>
                    </div>
                  </div>
                </Link>
                <div className="flex items-center gap-1">
                  {/* Athlete Pass Actions */}
                  {passStatus === 'none' && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleCreatePass(athlete.id)}
                      className="text-primary hover:text-primary/80"
                      title="Create Athlete Pass"
                    >
                      <Ticket size={16} className="mr-1" />
                      Create Pass
                    </Button>
                  )}
                  {passStatus === 'pending' && (
                    <>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleSendPaymentLink(athlete.id)}
                        className="text-blue-400 hover:text-blue-300"
                        title="Copy payment link"
                      >
                        <Send size={16} className="mr-1" />
                        Copy Link
                      </Button>
                      {athlete.parent_email && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleSendPaymentEmail(athlete.id, athlete.parent_email)}
                          className="text-green-400 hover:text-green-300"
                          title={`Send email to ${athlete.parent_email}`}
                        >
                          <Mail size={16} className="mr-1" />
                          Email
                        </Button>
                      )}
                      <span className="text-xs text-yellow-400 px-2 py-1 bg-yellow-500/10 rounded">
                        Awaiting Payment
                      </span>
                    </>
                  )}
                  {passStatus === 'active' && (
                    <span className="text-xs text-green-400 px-2 py-1 bg-green-500/10 rounded">
                      <Check size={12} className="inline mr-1" />
                      Active
                    </span>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(athlete.id, athlete.name)} className="text-muted-foreground hover:text-red-400">
                    <Trash2 size={18} />
                  </Button>
                  <Link to={`/coach/athletes/${athlete.id}`}>
                    <Button variant="ghost" size="icon">
                      <ArrowRight size={18} />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoachAthletes;
